/*
  Floating call / Viber buttons (snippets/cilek-float-contact.liquid).
  A button steps aside (class is-away) while a price, badge, call-to-action or form control
  is underneath it, so the float never covers buy-relevant content; it slides back as soon
  as that spot is clear again. A viewport IntersectionObserver keeps the list of on-screen
  candidates short, and the exact overlap is measured on scroll, resize and DOM changes.
  Items of horizontal rails count even where the rail clips them, so the float also stays
  clear of a price or badge that is about to be swiped into view.
*/
(function () {
  var root = document.querySelector('[data-cilek-float]');
  if (!root) return;
  if (!('IntersectionObserver' in window)) {
    root.classList.remove('is-pending');
    return;
  }

  var PAD = 6; // clearance around each button, px
  var COMPACT = 120; // buttons taller than this are media posters: only their text counts
  var MAX_HEIGHT = 600; // taller matches are page wrappers, skipped
  var TEXT_REACH = 40; // px around a price box where its text may still sit
  var BOX = 'button, .button, a[role="button"], input, select, textarea, .shopify-payment-button, [class*="badge"], [class*="swatch"], [class*="__cta"], [class*="-cta"]';
  var POSTER = 'button, .button, a[role="button"]';
  var WATCH = '[class*="price"], ' + BOX;
  var SKIP = '.cilek-float, .shopify-section-group-header-group, .visually-hidden';

  var onScreen = new Set();
  var railsOnScreen = new Set();
  var railItems = new Map(); // horizontal scroller -> its watched elements
  var scrollerCache = new WeakMap();
  var seen = new WeakSet();
  var watched = [];
  var ready = false;
  var observed = 0;
  var frame = 0;
  var burstUntil = 0;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) onScreen.add(e.target);
      else onScreen.delete(e.target);
    });
    ready = true;
    schedule();
  });
  var railIo = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) railsOnScreen.add(e.target);
      else railsOnScreen.delete(e.target);
    });
    schedule();
  });

  function isScroller(p) {
    if (scrollerCache.has(p)) return scrollerCache.get(p);
    var ox = getComputedStyle(p).overflowX;
    var yes = ox === 'auto' || ox === 'scroll' || ((ox === 'hidden' || ox === 'clip') && p.scrollWidth > p.clientWidth + 1);
    scrollerCache.set(p, yes);
    return yes;
  }

  function addToRail(el) {
    var rail = railOf(el);
    if (!rail) return;
    if (!railItems.has(rail)) {
      railItems.set(rail, []);
      railIo.observe(rail);
    }
    railItems.get(rail).push(el);
  }

  // which containers scroll sideways can change with the breakpoint
  function rebuildRails() {
    scrollerCache = new WeakMap();
    railItems.forEach(function (items, rail) {
      railIo.unobserve(rail);
    });
    railItems.clear();
    railsOnScreen.clear();
    watched = watched.filter(function (el) {
      return el.isConnected;
    });
    watched.forEach(addToRail);
  }

  function railOf(el) {
    for (var p = el.parentElement, i = 0; p && p !== document.body && p.tagName !== 'MAIN' && i < 12; p = p.parentElement, i++) {
      if (isScroller(p)) return p;
    }
    return null;
  }

  function collect() {
    var list = document.querySelectorAll(WATCH);
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (seen.has(el)) continue;
      seen.add(el);
      if (el === document.body || el.tagName === 'MAIN' || el.tagName === 'SECTION' || el.classList.contains('shopify-section')) continue;
      if (el.type === 'hidden' || el.closest(SKIP)) continue;
      io.observe(el);
      observed++;
      watched.push(el);
      addToRail(el);
    }
    onScreen.forEach(function (el) {
      if (!el.isConnected) onScreen.delete(el);
    });
    railItems.forEach(function (items, rail) {
      if (!rail.isConnected) {
        railItems.delete(rail);
        railsOnScreen.delete(rail);
        railIo.unobserve(rail);
      }
    });
  }

  function hit(a, b) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  }

  function textHit(el, zone) {
    var range = document.createRange();
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      if (!walker.currentNode.nodeValue.trim()) continue;
      range.selectNodeContents(walker.currentNode);
      var rects = range.getClientRects();
      for (var i = 0; i < rects.length; i++) {
        if (rects[i].width > 0 && hit(rects[i], zone)) return true;
      }
    }
    return false;
  }

  function under(el, zone) {
    if (!el.isConnected) return false;
    var r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2 || r.height > MAX_HEIGHT) return false;
    var whole = el.matches(BOX) && !(r.height > COMPACT && el.matches(POSTER));
    // text can overflow its box (e.g. the screen-reader price label), so text checks look a bit wider
    var near = whole ? r : { left: r.left - TEXT_REACH, top: r.top - TEXT_REACH, right: r.right + TEXT_REACH, bottom: r.bottom + TEXT_REACH };
    if (!hit(near, zone) || getComputedStyle(el).visibility === 'hidden') return false;
    return whole ? true : textHit(el, zone);
  }

  function covered(zone) {
    var found = false;
    onScreen.forEach(function (el) {
      if (!found && under(el, zone)) found = true;
    });
    railsOnScreen.forEach(function (rail) {
      if (found) return;
      var rr = rail.getBoundingClientRect();
      if (!hit({ left: -1e5, right: 1e5, top: rr.top - TEXT_REACH, bottom: rr.bottom + TEXT_REACH }, zone)) return;
      var items = railItems.get(rail) || [];
      for (var i = 0; i < items.length && !found; i++) {
        if (under(items[i], zone)) found = true;
      }
    });
    return found;
  }

  function check() {
    frame = 0;
    if (performance.now() < burstUntil) schedule(); // content is animating: re-check every frame
    var box = root.getBoundingClientRect();
    if (!box.width || !box.height || !ready) return; // hidden by CSS (cookie banner, open drawer) or not measured yet
    // queried each time, so a button added later (e.g. a Viber number) is handled too
    Array.prototype.forEach.call(root.querySelectorAll('.cilek-float__btn'), function (btn) {
      // layout position, unaffected by the slide-out transform
      var left = box.left + btn.offsetLeft;
      var top = box.top + btn.offsetTop;
      var zone = {
        left: left - PAD,
        top: top - PAD,
        right: left + btn.offsetWidth + PAD,
        bottom: top + btn.offsetHeight + PAD,
      };
      // a keyboard-focused button stays put so focus is never lost
      btn.classList.toggle('is-away', btn !== document.activeElement && covered(zone));
    });
    root.classList.remove('is-pending');
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(check);
  }

  // scroll-in animations and transitions move content without scrolling
  function burst() {
    burstUntil = performance.now() + 1000;
    schedule();
  }

  var collectTimer = 0;
  function scheduleCollect() {
    if (collectTimer) return;
    collectTimer = setTimeout(function () {
      collectTimer = 0;
      collect();
      schedule();
    }, 200);
  }

  collect();
  if (!observed) {
    ready = true;
    schedule();
  }
  root.addEventListener('focusout', schedule);
  document.addEventListener('scroll', schedule, { passive: true, capture: true });
  var widthNow = window.innerWidth;
  var railTimer = 0;
  window.addEventListener('resize', function () {
    schedule();
    if (window.innerWidth === widthNow) return; // phone toolbars change the height only
    widthNow = window.innerWidth;
    clearTimeout(railTimer);
    railTimer = setTimeout(function () {
      rebuildRails();
      schedule();
    }, 300);
  }, { passive: true });
  document.addEventListener('animationstart', burst, true);
  document.addEventListener('transitionrun', burst, true);
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);
  document.addEventListener('click', function () {
    setTimeout(schedule, 350);
  }, true);
  if ('ResizeObserver' in window) {
    var ro = new ResizeObserver(schedule);
    ro.observe(root);
    ro.observe(document.body);
  }
  new MutationObserver(scheduleCollect).observe(document.body, { childList: true, subtree: true });
})();
